import {ChangeDetectorRef, EventEmitter, HostBinding, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {NotificationsService} from 'angular2-notifications';
import {RestService} from 'gelios-ng-admin-components';
import {AuthorizationService} from '../authorization/authorization.service';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {ReflexiveRestModel} from 'src/lib/reflexive-model/reflexive-rest-model';
import {TranslateService} from '@ngx-translate/core';
import {ImmutableListRepositoryService} from '../../lib/gelios-field/immutable-list-repository.service';
import {debounceTime, delay, filter, take} from 'rxjs/operators';
import {RouteLibraryService} from '../route/route-library.service';
import {RouteLibraryEntity} from '../route/route-library-entity';
import {ProjectBusinessLogicFilterService} from '../shared/project-business-logic-filter/project-business-logic-filter.service';

export abstract class BaseFormContainer implements OnInit, OnDestroy {

  public static readonly modeUpdate = 'update';
  public static readonly modeCreate = 'create';
  public readonly modeUpdate = BaseFormContainer.modeUpdate;
  public readonly modeCreate = BaseFormContainer.modeCreate;

  @HostBinding('class.form-and-buttons-container') classFormAndButtonsContainer = true;

  @Output() refreshBreadcrumbsEvent: EventEmitter<string | number>;
  @Output() refreshActionsEvent: EventEmitter<number>;
  @Output() titleChanged: EventEmitter<string>;
  @Output() refreshData: EventEmitter<any>;
  subscriptions: Subscription[] = [];
  protected createButtonCaption = 'Save';
  protected saveSuccessMessage = 'Saved';
  protected confirmationMessage: string | null = null;
  busy: BehaviorSubject<{busy: boolean}> = new BehaviorSubject<{busy: boolean}>({busy: false});
  formGroup: FormGroup;
  formGroupReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  routeEntity: RouteLibraryEntity;

  model: BehaviorSubject<ReflexiveRestModel> = new BehaviorSubject(null);
  protected service: RestService<ReflexiveRestModel>;
  modelName: string;
  modelFieldNames: Array<string>;
  formFieldNames: Array<string>;
  formErrorMessages: { [message: string]: string } = {
    passwordsDoNotMatch: 'Passwords do not match',
    passwordIsRequired: 'Password is required'
  };
  lastSavedEntityId: number;
  protected get snapshotParamId() {
    return this.activatedRoute.snapshot.params.id;
  }

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected notif: NotificationsService,
    protected formBuilder: FormBuilder,
    protected authorizationService: AuthorizationService,
    protected http: HttpClient,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
    protected changeDetector: ChangeDetectorRef,
    protected translates: TranslateService,
    protected immutableList: ImmutableListRepositoryService,
    protected routeLibrary: RouteLibraryService,
    protected projectBusinessLogicFilterService: ProjectBusinessLogicFilterService,
  ) {
    this.refreshBreadcrumbsEvent = new EventEmitter();
    this.refreshActionsEvent = new EventEmitter();
    this.titleChanged = new EventEmitter();
    this.refreshData = new EventEmitter();
    this.service = this.createService(http, environmentVersion, environmentApiBasepoint);
    this.modelName = this.getModelName();
  }

  abstract createService(http: HttpClient, environmentVersion: string, environmentApiBasepoint: string): RestService<any>;

  abstract createModel(): any;

  abstract getModelName(): string;


  getMode(): string {
    return (this.activatedRoute.snapshot.params && this.snapshotParamId) ? BaseFormContainer.modeUpdate : BaseFormContainer.modeCreate;
  }

  fetchModel() {
    if (this.getMode() === this.modeCreate) {
      this.model.next(this.createModel());
    } else {
      this.fetchModelFromBackend();
    }
  }

  protected fetchModelFromBackend() {
    this.service.view(this.snapshotParamId).subscribe(
      response => {
        this.model.next((this.createModel()).loadFromServerData(response.body));
      }
    );
  }

  fetchFieldNames() {
    if (this.modelName === AuthorizationService.ENTITY_USER) {
      if (this.getMode() === BaseFormContainer.modeCreate) {
        this.modelFieldNames =
          this.authorizationService.getEntityAccessRightsInstant()
            [AuthorizationService.ENTITY_USER][AuthorizationService.ACTION_CREATE];
      } else {
        const userIdToEdit = parseInt(this.snapshotParamId, 10);
        const loggedIdUserId = this.authorizationService.getIdentity().id;
        if (loggedIdUserId === userIdToEdit) {
          this.modelFieldNames =
            this.authorizationService.getEntityAccessRightsInstant()
              [AuthorizationService.ENTITY_CURRENT_USER][AuthorizationService.ACTION_UPDATE];
        } else {
          this.modelFieldNames =
            this.authorizationService.getEntityAccessRightsInstant()
              [AuthorizationService.ENTITY_USER][AuthorizationService.ACTION_UPDATE];
        }
      }
    } else {
      this.modelFieldNames = this.authorizationService.getEntityAccessRightsInstant()[this.modelName][this.getMode()];
    }
    const tempModel = this.createModel();
    this.modelFieldNames = this.modelFieldNames.filter(fn => fn in tempModel.fields);
    this.formFieldNames = [...this.modelFieldNames];
  }

  resetBeforeRouterRedirect() {
    if (this.model.getValue()) {
      this.model.getValue().onDestroy();
    }
    this.model.next(null);
  }

  protected fetchRequiredData(): Observable<any> {
    return of(null);
  }

  updateForm() {
    this.fetchRequiredData().pipe(take(1)).subscribe(
      () => {
        this.fetchFieldNames();
        this.fetchModel();
      }
    );
  }

  routerEvent() {
    this.subscriptions.push(
      this.router.events.subscribe(
        e => {
          this.resetBeforeRouterRedirect();
          if (e instanceof NavigationEnd) {
            this.updateForm();
          }
        }
      )
    );
  }

  ngOnInit() {
//    const groupConfig = (new RestModel()).getFormGroupConfig(this.fieldNames);
//    this.formGroup = this.formBuilder.group(groupConfig);
    this.updateForm();
    this.model.subscribe(
      userModel => {
        this.formGroup =
          userModel ?
            userModel.getFormGroup(this.formBuilder, this.formFieldNames)
            : null;
        this.formGroupReady.next(!!this.formGroup);
      }
    );
    if (this.activatedRoute.snapshot.routeConfig.data && this.activatedRoute.snapshot.routeConfig.data.title) {
      this.titleChanged.emit(this.translates.instant(this.activatedRoute.snapshot.routeConfig.data.title));
    }
    this.routerEvent();
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
        control.updateValueAndValidity();
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      } else if (control instanceof FormArray) {
        control.markAsTouched({onlySelf: true});
        control.updateValueAndValidity();
        (control as FormArray).controls.forEach(aControl => {
          if (aControl instanceof FormControl) {
            aControl.markAsTouched({onlySelf: true});
            aControl.updateValueAndValidity();
          } else if (aControl instanceof FormGroup) {
            this.validateAllFormFields(aControl);
          }
        });
      }
    });
  }

  validate() {
    this.validateAllFormFields(this.formGroup);
    this.formGroup.markAsTouched();
  }

  onSubmitSuccess(response: HttpResponse<any>) { }

  submit() {
    this.busy.next({busy: true});
    if (this.confirmationMessage) {
      if (!window.confirm(this.translates.instant(this.confirmationMessage))) {
        return;
      }
    }
    const subscription = this.formGroup.statusChanges
      .pipe(
        delay(0),
        filter(status => status !== 'PENDING'),
        debounceTime(500),
      ).subscribe(
        status => {
          subscription.unsubscribe();
          if (status === 'VALID') {
            const model = this.createModel().loadFromAngularFormGroup(this.formGroup);
            const restData = model.prepareDataForRest(this.modelFieldNames);
            let method: (model: any) => Observable<any> = this.service.create;
            if (this.getMode() === BaseFormContainer.modeUpdate) {
              restData.id = this.snapshotParamId;
              method = this.service.update;
            }
            this.busy.next({busy: true});
            method.call(this.service, restData).subscribe(
              (data: HttpResponse<any>) => {
                this.busy.next({busy: false});
                this.onSubmitSuccess(data);
                if (data.body.id || data.body.ids || data.body.name) {
                  if (data.body.id) {
                    this.lastSavedEntityId = data.body.id;
                    if (this.getMode() === BaseFormContainer.modeUpdate) {
                      this.refreshBreadcrumbs(this.lastSavedEntityId);
                    }
                    if (this.getMode() === BaseFormContainer.modeCreate
                      && this.routeLibrary.routeHasAction(this.routeEntity, 'view')) {
                      this.refreshActions(this.lastSavedEntityId);
                    }
                  }
                  this.busy.next({busy: false});
                  this.successNavigate();
                  // if (methodCreate) {
                  this.refreshData.emit();
                  // }
                  this.notif.success(this.translates.instant(this.saveSuccessMessage));
                } else {
                  this.notif.error(this.translates.instant('Server Error'));
                  console.error('Unknown error');
                }
              },
              (response: HttpErrorResponse) => this.handleSubmitError(response)
            );
          } else {
            this.busy.next({busy: false});
            this.notif.alert(this.translates.instant('Form validation error'));
            console.log(this.formGroup, this.formGroup.errors);
          }
        }
      );
    this.validateAllFormFields(this.formGroup);
  }

  handleSubmitError(response: HttpErrorResponse): void {
    this.busy.next({busy: false});
    switch (response.status) {
      case 422:
        const errors = response.error;
        if (!errors || errors.length === 0) {
          this.notif.error(this.translates.instant('Form validation error'));
          console.warn(errors);
        } else {
          errors.map(
            error => {
              const control = this.formGroup.get(error.field);
              if (control) {
                control.setErrors({serverError: error.message}, {emitEvent: true});
              }
              this.notif.error(error.message);
            }
          );
        }
        break;
      // Todo: Move to http interceptor
      case 500:
        this.notif.error(this.translates.instant('Server error'));
        console.error(response.error);
        break;
      default:
        this.notif.error(this.translates.instant('Unknown server error'));
        console.error(response.error);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(v => v.unsubscribe());
  }

  cancelNavigate() {
    this.successNavigate();
  }

  get routeFirstUrlPathId() {
    return (this.activatedRoute
      && this.activatedRoute.snapshot
      && this.activatedRoute.snapshot.url
      && this.activatedRoute.snapshot.url[0]
      && parseInt(this.activatedRoute.snapshot.url[0].path, 10)) || null;
  }

  successNavigate() {
    if ((this.lastSavedEntityId || this.routeFirstUrlPathId)
      && this.routeLibrary.routeHasAction(this.routeEntity, 'view')) {
      this.router.navigateByUrl(this.routeLibrary.getRoute({
        entity: this.routeEntity,
        action: 'view',
        id: this.lastSavedEntityId || this.routeFirstUrlPathId
      }));
    } else {
      this.router.navigateByUrl(this.routeLibrary.getRoute({
        entity: this.routeEntity,
        action: 'list',
      }));
    }
  }

  refreshBreadcrumbs(id: number) {
    this.refreshBreadcrumbsEvent.emit(id);
  }

  refreshActions(id: number) {
    this.refreshActionsEvent.emit(id);
  }
}
